import config from 'src/config';

const IMPACT_CLICK_ID_KEY = 'impact_click_id';
const IMPACT_PARTNER_ID_KEY = 'impact_partner_id';
const IMPACT_PROFILE_ID_KEY = 'impact_custom_profile_id';
const IMPACT_SCRIPT_ID = 'impact-universal-tracking-tag';

declare global {
  interface Window {
    ire?: (...args: any[]) => void;
    ire_o?: string;
  }
}

const getMaxAge = () =>
  Math.max(1, config.impactReferralWindowDays || 30) * 24 * 60 * 60;

const setCookie = (name: string, value: string) => {
  if (!value) {
    return;
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Max-Age=${getMaxAge()}; Path=/; SameSite=Lax${secure}`;
};

const getCookie = (name: string) => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
};

const storeValue = (name: string, value: string) => {
  if (!value) {
    return;
  }

  try {
    localStorage.setItem(name, value);
  } catch (error) {
    // Browser storage can be unavailable in private modes; cookie remains as fallback.
  }

  setCookie(name, value);
};

const getStoredValue = (name: string) => {
  try {
    return localStorage.getItem(name) || getCookie(name);
  } catch (error) {
    return getCookie(name);
  }
};

const getUrlImpactClickId = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get('im_ref') || urlParams.get('clickid') || '';
  } catch (error) {
    return '';
  }
};

const getUrlImpactPartnerId = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get('irpid') || '';
  } catch (error) {
    return '';
  }
};

const generateFallbackUuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;

    return value.toString(16);
  });

const getImpactProfileId = () => {
  const existingProfileId = getStoredValue(IMPACT_PROFILE_ID_KEY);

  if (existingProfileId) {
    return existingProfileId;
  }

  const profileId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : generateFallbackUuid();

  storeValue(IMPACT_PROFILE_ID_KEY, profileId);

  return profileId;
};

const sha1Hex = async (value: string) => {
  const normalizedValue = value.trim().toLowerCase();

  if (!normalizedValue || !window.crypto?.subtle) {
    return '';
  }

  const encodedValue = new TextEncoder().encode(normalizedValue);
  const digest = await window.crypto.subtle.digest('SHA-1', encodedValue);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const bootstrapImpactUtt = () => {
  if (
    !config.impactUniversalTrackingTagUrl ||
    window.ire ||
    document.getElementById(IMPACT_SCRIPT_ID)
  ) {
    return;
  }

  window.ire_o = 'ire';
  window.ire = function (...args: any[]) {
    ((window.ire as any).a = (window.ire as any).a || []).push(args);
  };

  const script = document.createElement('script');
  const firstScript = document.getElementsByTagName('script')[0];

  script.async = true;
  script.id = IMPACT_SCRIPT_ID;
  script.src = config.impactUniversalTrackingTagUrl;
  firstScript.parentNode?.insertBefore(script, firstScript);
};

const requestGeneratedClickId = () => {
  if (!window.ire) {
    return;
  }

  try {
    window.ire('generateClickId', (clickId: string) => {
      storeValue(IMPACT_CLICK_ID_KEY, clickId);
    });
  } catch (error) {
    // UTT can be blocked by extensions; checkout still works without attribution.
  }
};

export const initImpactTracking = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const impactClickId = getUrlImpactClickId();

  if (impactClickId) {
    storeValue(IMPACT_CLICK_ID_KEY, impactClickId);
  }

  const impactPartnerId = getUrlImpactPartnerId();

  if (impactPartnerId) {
    storeValue(IMPACT_PARTNER_ID_KEY, impactPartnerId);
  }

  getImpactProfileId();
  bootstrapImpactUtt();
  requestGeneratedClickId();
};

export const getImpactClickId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const impactClickId =
    getUrlImpactClickId() || getStoredValue(IMPACT_CLICK_ID_KEY);

  if (impactClickId) {
    storeValue(IMPACT_CLICK_ID_KEY, impactClickId);
  }

  return impactClickId;
};

export const getImpactPartnerId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const impactPartnerId =
    getUrlImpactPartnerId() || getStoredValue(IMPACT_PARTNER_ID_KEY);

  if (impactPartnerId) {
    storeValue(IMPACT_PARTNER_ID_KEY, impactPartnerId);
  }

  return impactPartnerId;
};

export const identifyImpactVisitor = async (currentUser?: any) => {
  if (typeof window === 'undefined' || !window.ire) {
    return;
  }

  try {
    const customerEmail = currentUser?.email
      ? await sha1Hex(currentUser.email)
      : '';

    window.ire('identify', {
      customerid: currentUser?.id || '',
      customeremail: customerEmail,
      customProfileId: getImpactProfileId(),
    });
  } catch (error) {
    // Tracking failures should never block the app experience.
  }
};
